/**
 * CloseReasonPicker · v101.101.0 · Motivo de cierre de la OT (uno o varios) + descripción.
 *  · CloseReasonDialog: ventana para elegir/guardar (se abre sola antes de «Validar/Cerrar» si falta).
 *  · CloseReasonCard: muestra el motivo registrado en la OT (pestaña General) con «Editar».
 * Catálogo: /admin/motivos-cierre (GET/POST /v1/close-reasons).
 */
import { useEffect, useState } from "react";
import api, { extractApiError } from "../lib/api";
import { toast } from "sonner";
import { ClipboardList, Loader2, X, CheckSquare, Square, Edit3 } from "lucide-react";

export async function fetchCloseReasons(odooTaskId) {
  const { data } = await api.get(`/v1/close-reasons/order/${odooTaskId}`);
  return data;
}

export function CloseReasonDialog({ odooTaskId, open, onClose, onSaved, title = "Motivo de cierre" }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !odooTaskId) return;
    setLoading(true);
    fetchCloseReasons(odooTaskId)
      .then((d) => {
        setInfo(d);
        setCodes((d?.selected?.reasons || []).map((r) => r.code));
        setDesc(d?.selected?.description || "");
      })
      .catch((e) => toast.error(extractApiError ? extractApiError(e) : "No se pudieron cargar los motivos"))
      .finally(() => setLoading(false));
  }, [open, odooTaskId]);

  if (!open) return null;
  const opts = info?.options || [];
  const minDesc = info?.min_description || 10;
  const needsDesc = opts.some((o) => codes.includes(o.code) && o.requires_description);
  const canSave = codes.length > 0 && (!needsDesc || desc.trim().length >= minDesc) && !saving;
  const toggle = (c) => setCodes((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/v1/close-reasons/order/${odooTaskId}`, { codes, description: desc });
      toast.success("Motivo de cierre guardado");
      onSaved && onSaved(data?.selected);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar el motivo de cierre");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="close-reason-dialog"
         onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} className="text-slate-700" />
            <div>
              <div className="text-sm font-bold text-slate-900">{title}</div>
              <div className="text-[11px] text-slate-500">Seleccione uno o varios motivos y describa el cierre.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={14} className="animate-spin" /> Cargando motivos…</div>
          ) : opts.length === 0 ? (
            <div className="text-xs text-slate-500">No hay motivos de cierre configurados para esta actividad.</div>
          ) : (
            <>
              <div className="space-y-1.5">
                {opts.map((o) => {
                  const on = codes.includes(o.code);
                  return (
                    <button key={o.code} type="button" onClick={() => toggle(o.code)}
                      data-testid={`close-reason-opt-${o.code}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-sm transition ${on ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                      {on ? <CheckSquare size={16} className="text-emerald-600 shrink-0" /> : <Square size={16} className="text-slate-400 shrink-0" />}
                      <span>{o.label}</span>
                    </button>
                  );
                })}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
                  Descripción {needsDesc ? `(mínimo ${minDesc} caracteres)` : "(opcional)"}
                </label>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} maxLength={2000}
                  data-testid="close-reason-description"
                  placeholder="Qué se hizo / por qué se cierra así"
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm outline-none focus:border-emerald-500" />
                <div className="text-[10px] text-slate-400 text-right">{desc.trim().length} caracteres</div>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-xl text-xs font-bold border border-slate-300 hover:bg-slate-50">Cancelar</button>
          <button onClick={save} disabled={!canSave} data-testid="close-reason-save"
            className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1.5">
            {saving && <Loader2 size={13} className="animate-spin" />} Guardar motivo
          </button>
        </div>
      </div>
    </div>
  );
}

export function CloseReasonCard({ odooTaskId, canEdit, closed, refreshKey }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { if (odooTaskId) fetchCloseReasons(odooTaskId).then(setInfo).catch(() => setInfo(null)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [odooTaskId, refreshKey]);
  if (!info || (!info.required && !(info.selected?.reasons || []).length)) return null;
  const sel = info.selected || {};
  const has = (sel.reasons || []).length > 0;
  return (
    <div className="rounded-3xl p-4" style={{ background: has ? "#F0FDF4" : "#FFFBEB", border: `1.5px solid ${has ? "#BBF7D0" : "#FDE68A"}` }}
         data-testid="card-close-reason">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-800"><ClipboardList size={14} /> Motivo de cierre</div>
        {canEdit && !closed && (
          <button onClick={() => setOpen(true)} className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1"
                  data-testid="btn-edit-close-reason">
            <Edit3 size={12} /> {has ? "Editar" : "Registrar"}
          </button>
        )}
      </div>
      {has ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {sel.reasons.map((r) => (
              <span key={r.code} className="px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-emerald-100 text-emerald-900">{r.label}</span>
            ))}
          </div>
          {sel.description && <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap">{sel.description}</div>}
          <div className="mt-1 text-[10px] text-slate-400">{sel.by || ""}{sel.at ? ` · ${new Date(sel.at).toLocaleString()}` : ""}</div>
        </>
      ) : (
        <div className="text-xs text-amber-800">Pendiente: el técnico aún no registró el motivo de cierre.</div>
      )}
      <CloseReasonDialog odooTaskId={odooTaskId} open={open} onClose={() => setOpen(false)}
        onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
